import {useConfig} from '@aboutyou/config';
import {
  useActivateApiAbTestCallback,
  useVariantActiveForAbTestBackend,
} from '@aboutyou/feature-flags';
import {ProductTilePreferences} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product_tile_preferences.types';
import {SpotlightSection} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/sections.types';
import {
  GetBrowseSectionsNextResponse,
  GetBrowseSectionsResponse,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/navigation_page/v1/service.types';
import {useBrandsOverviewPage, useFavouritesPage} from '@aboutyou/router';
import {InView} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {ComponentType, memo} from 'react';
import {HeroBrandsSlider} from '../../../features/HubPage/Sections/HeroBrandsSlider/HeroBrandsSlider';
import {CustomComponents} from '../../../features/HubPage/types';
import {HomePageGridAreaNames} from '../utils/gridAreaNames';
import {BodyTypesSection} from './BodyTypesSection';
import {CategoryHighlightSection} from './CategoryHighlightSection/CategoryHighlightSection';
import {InspirationGridSection} from './InspirationGridSection/InspirationGridSection';
import {ProductsSection} from './ProductsSection';
import {StorySection} from './StorySection/StorySection';
import {TargetGroup} from './TargetGroup';

type itemsType = Array<
  GetBrowseSectionsResponse | GetBrowseSectionsNextResponse
>;

interface StreamComponentListProps
  extends Omit<CustomComponents, 'SliderComponent' | 'StreamTileList'> {
  items: itemsType;
  gridColumns: number | undefined;
  itemsPreferences: ProductTilePreferences | undefined;
  SliderComponent: ComponentType<SpotlightSection & {isFetching?: boolean}>;
  onAddToBasketCtaClick?: ({productId}: {productId: number}) => void;
  isFetching?: boolean;
}

const StreamComponentList: React.FC<StreamComponentListProps> = ({
  items,
  SliderComponent,
  onAddToBasketCtaClick,
  isFetching,
}) => {
  const {device} = useConfig();
  const activateApiAbTest = useActivateApiAbTestCallback();
  const isVariantActiveEntryPrice = useVariantActiveForAbTestBackend({
    experimentKey: 'ab_entry_price_reco',
  });
  const brandsOverviewPage = useBrandsOverviewPage();
  const favouritesPage = useFavouritesPage();

  return (
    <>
      {items.map((item, index) => {
        switch (item.section.type?.$case) {
          case 'targetGroup': {
            if (device === 'desktop') {
              return null;
            }

            return (
              <TargetGroupGridAreaWrapper data-without-gap>
                <TargetGroup
                  key={`browse-targetGroup-${index}`}
                  {...item.section.type.targetGroup}
                />
              </TargetGroupGridAreaWrapper>
            );
          }
          case 'spotlight':
            return (
              <SpotlightGridAreaWrapper data-without-gap>
                <SliderComponent
                  key={`browse-spotlight-${index}`}
                  elements={item.section.type.spotlight.elements}
                  isFetching={isFetching}
                />
              </SpotlightGridAreaWrapper>
            );
          case 'recommendedBrands':
            return (
              <HeroBrandsSlider
                key={`recommended-brands-${index}`}
                trackProductListSwipeEvent
                streamPosition={index}
                onAddToBasketCtaClick={onAddToBasketCtaClick}
                allBrandsUrl={brandsOverviewPage}
                {...item.section.type.recommendedBrands}
              />
            );
          case 'popularBrands':
            return (
              <HeroBrandsSlider
                key={`popular-brands-${index}`}
                trackProductListSwipeEvent
                streamPosition={index}
                onAddToBasketCtaClick={onAddToBasketCtaClick}
                allBrandsUrl={brandsOverviewPage}
                {...item.section.type.popularBrands}
              />
            );
          case 'suggestedBrands':
            return (
              <HeroBrandsSlider
                key={`suggested-brands-${index}`}
                trackProductListSwipeEvent
                streamPosition={index}
                onAddToBasketCtaClick={onAddToBasketCtaClick}
                allBrandsUrl={brandsOverviewPage}
                topline={item.section.type.suggestedBrands.subline}
                {...item.section.type.suggestedBrands}
              />
            );
          case 'favouriteBrands':
            return (
              <HeroBrandsSlider
                key={`favourite-brands-${index}`}
                trackProductListSwipeEvent
                streamPosition={index}
                onAddToBasketCtaClick={onAddToBasketCtaClick}
                topline={item.section.type.favouriteBrands.subline}
                allBrandsUrl={favouritesPage}
                {...item.section.type.favouriteBrands}
              />
            );
          case 'products':
            {
              const product = item.section.type.products;
              return (
                <MarginRemovalWrapper>
                  <InView
                    onChange={inView => {
                      if (
                        inView &&
                        isVariantActiveEntryPrice &&
                        product.promotionTracker.locationId?.includes(
                          'salesPickProducts',
                        )
                      ) {
                        activateApiAbTest('ab_entry_price_reco', {
                          triggerMultipleTimesPerSession: true,
                        });
                      }
                    }}
                    threshold={0.8}
                    triggerOnce
                  >
                    <ProductsSection
                      key={`products-${index}`}
                      streamPosition={index}
                      onAddToBasketCtaClick={onAddToBasketCtaClick}
                      {...item.section.type.products}
                    />
                  </InView>
                </MarginRemovalWrapper>
              );
            }
            break;
          case 'categoryHighlight':
            return (
              <CategoryHighlightSection
                key={`categories-${index}`}
                streamPosition={index}
                {...item.section.type.categoryHighlight}
              />
            );
          case 'story': {
            return (
              <StorySection
                key={`story-${index}`}
                streamPosition={index}
                {...item.section.type.story}
              />
            );
          }
          // TODO: Temporarily hidden because the "Discover more" CTA leads to an empty page. Needs further iteration. https://aboutyou.atlassian.net/browse/ASFE-13311
          // case 'fashionUpdateTeaser':
          // return (
          //   <FashionUpdate
          //     key={`fashion-update-teaser-${index}`}
          //     streamPosition={index}
          //     onAddToBasketCtaClick={onAddToBasketCtaClick}
          //     {...item.section.type.fashionUpdateTeaser}
          //   />
          // );
          case 'bodyTypes':
            return (
              <BodyTypesSection
                key={`body-types-${index}`}
                streamPosition={index}
                {...item.section.type.bodyTypes}
              />
            );
          case 'inspirationGrid':
            return (
              <InspirationGridSection
                key={`inspiration-grid-${index}`}
                streamPosition={index}
                {...item.section.type.inspirationGrid}
              />
            );
          case 'celebrityCollections':
            return (
              <HeroBrandsSlider
                key={`celebrity-collections-${index}`}
                trackProductListSwipeEvent
                streamPosition={index}
                onAddToBasketCtaClick={onAddToBasketCtaClick}
                topline={item.section.type.celebrityCollections.subline}
                {...item.section.type.celebrityCollections}
              />
            );
          default:
            return null;
        }
      })}
    </>
  );
};

const MarginRemovalWrapper = styled.div`
  // added to reset margin-bottom introduce in packages/core/application-desktop/src/app/components/pages/HomePage/HomePage.eager.tsx
  // we are using global selector that is impacting any element in this level of list
  // selector used:   > * { <styles> }
  &&& {
    margin-bottom: 0;
  }
`;

const TargetGroupGridAreaWrapper = styled.div`
  grid-area: ${HomePageGridAreaNames.TARGET_GROUP};
`;

const SpotlightGridAreaWrapper = styled.div`
  grid-area: ${HomePageGridAreaNames.SPOTLIGHT};
`;

const StreamComponentListMemo = memo(StreamComponentList);
export {StreamComponentListMemo as StreamComponentList};
